import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { VERIFICATION_STATE, VerificationState } from 'utils/documentVerification';

import styles from './styles.module.scss';

const icons: Record<VerificationState, { src: string; whiten: boolean }> = {
  [VERIFICATION_STATE.NONE]: {
    src: new URL('../../../../img/uploading-marker.svg', import.meta.url).href,
    whiten: true,
  },
  [VERIFICATION_STATE.ACTION]: {
    src: new URL('../../../../img/info-outline-white.svg', import.meta.url).href,
    whiten: false,
  },
  [VERIFICATION_STATE.PENDING]: {
    src: new URL('../../../../img/clock.svg', import.meta.url).href,
    whiten: true,
  },
  [VERIFICATION_STATE.REJECTED]: {
    src: new URL('../../../../img/close.svg', import.meta.url).href,
    whiten: true,
  },
  [VERIFICATION_STATE.VERIFIED]: {
    src: new URL('../../../../img/check-white.svg', import.meta.url).href,
    whiten: false,
  },
};

interface VerificationBannerProps {
  state: VerificationState;
  /** Названия недостающих обязательных документов */
  missing?: string[];
  /** Причина отклонения от модератора */
  comment?: string | null;
  /** Кнопка «Отправить на проверку» — показываем только владельцу и только когда можно отправить */
  onSubmit?: () => void;
  isSubmitting?: boolean;
  submitDisabled?: boolean;
}

/** Баннер статуса верификации: пять состояний из макета. */
const VerificationBanner = ({
  state,
  missing = [],
  comment,
  onSubmit,
  isSubmitting = false,
  submitDisabled = false,
}: VerificationBannerProps) => {
  const { t } = useTranslation();
  const icon = icons[state];
  const showMissing = state === VERIFICATION_STATE.ACTION && missing.length > 0;
  const showComment = state === VERIFICATION_STATE.REJECTED && Boolean(comment);

  return (
    <div className={cn(styles.statusBanner, styles[`statusBanner--${state}`])}>
      <div className={cn(styles.statusBannerIcon, styles[`statusBannerIcon--${state}`])}>
        <img
          className={cn(styles.statusBannerIconImg, { [styles.statusBannerIconWhite]: icon.whiten })}
          src={icon.src}
          alt=""
        />
      </div>
      <div className={styles.statusBannerContent}>
        <div className={styles.statusBannerTitle}>
          <span className={styles.statusBannerTitleText}>
            {t(`documents.verification.${state}.title`)}
          </span>
          <span className={cn(styles.statusBannerTag, styles[`statusBannerTag--${state}`])}>
            {showMissing
              ? t('documents.required_count', { count: missing.length })
              : t(`documents.verification.${state}.tag`)}
          </span>
        </div>
        {showMissing ? (
          <>
            <span className={styles.statusBannerDescription}>
              {t('documents.verification.missing_title')}
            </span>
            <ul className={styles.missingList}>
              {missing.map((label) => (
                <li className={styles.missingItem} key={label}>
                  {label}
                </li>
              ))}
            </ul>
          </>
        ) : (
          <span className={styles.statusBannerDescription}>
            {t(`documents.verification.${state}.description`)}
          </span>
        )}
        {showComment && (
          <span className={styles.statusBannerComment}>
            <span className={styles.statusBannerCommentLabel}>
              {t('documents.verification.reject_reason')}
            </span>
            {comment}
          </span>
        )}
        {onSubmit && (
          <button
            className={styles.statusBannerButton}
            type="button"
            onClick={onSubmit}
            disabled={submitDisabled || isSubmitting}
          >
            {t(
              isSubmitting
                ? 'documents.verification.submit_pending'
                : 'documents.verification.submit_button',
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default VerificationBanner;
